import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';
import api from '../services/api';
import './ClassMarksPrint.css';

interface StudentMarks {
  id: number;
  serial_number: number;
  candidate_name: string;
  sex: string;
  history_score: number | null;
  geography_score: number | null;
  kiswahili_score: number | null;
  english_score: number | null;
  physics_score: number | null;
  chemistry_score: number | null;
  biology_score: number | null;
  basic_math_score: number | null;
  bookkeeping_score: number | null;
  commerce_score: number | null;
  bible_knowledge_score: number | null;
  civics_score: number | null;
  agriculture_score: number | null;
  arabic_score: number | null;
  sports_score: number | null;
  french_score: number | null;
  total_score: number | null;
  average_score: number | null;
  division: string | null;
  points: number | null;
  position: number | null;
  history_grade: string | null;
  geography_grade: string | null;
  kiswahili_grade: string | null;
  english_grade: string | null;
  physics_grade: string | null;
  chemistry_grade: string | null;
  biology_grade: string | null;
  basic_math_grade: string | null;
  bookkeeping_grade: string | null;
  commerce_grade: string | null;
  bible_knowledge_grade: string | null;
  civics_grade: string | null;
  agriculture_grade: string | null;
  arabic_grade: string | null;
  sports_grade: string | null;
  french_grade: string | null;
}

interface SubjectSummary {
  subject_name: string;
  grade_a_count: number;
  grade_b_count: number;
  grade_c_count: number;
  grade_d_count: number;
  grade_f_count: number;
  total_candidates: number;
  total_passed: number;
  total_failed: number;
  pass_percentage: number;
  average_score: number | null;
}

interface OverallSummary {
  total_students: number;
  total_male: number;
  total_female: number;
  class_average: number | null;
  class_gpa: number | null;
  division_counts: Record<string, number>;
  pass_count: number;
  fail_count: number;
  pass_percentage: number;
  subject_averages: Record<string, number>;
}

interface ClassMarksRecord {
  id: number;
  school_name: string;
  school_registration_number: string;
  class_display: string;
  term_display: string;
  exam_type_display: string;
  exam_year: number;
  exam_month: string;
  center_number: string;
  center_name: string;
  total_students: number;
  status: string;
  status_display: string;
  created_at: string;
  updated_at: string;
  user_name: string;
  user_email: string;
  subjects: string[];
  students: StudentMarks[];
  subject_summaries: SubjectSummary[];
  overall_summary: OverallSummary;
}

const ClassMarksPrint: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const printRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(true);
  const [record, setRecord] = useState<ClassMarksRecord | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [subjects, setSubjects] = useState<{ key: string; label: string }[]>([]);

  useEffect(() => {
    fetchRecord();
  }, [id]);

  const fetchRecord = async () => {
    try {
      const response = await api.get(`/classmarks/records/${id}/`);
      setRecord(response.data);
      
      if (response.data.subjects && response.data.subjects.length > 0) {
        const subjectList = response.data.subjects.map((sub: string) => {
          const label = sub.replace('_score', '').replace('_', ' ').toUpperCase();
          return { key: sub, label };
        });
        setSubjects(subjectList);
      }
    } catch (error) {
      console.error('Error fetching record:', error);
      setError('Failed to load marks record');
    } finally {
      setLoading(false);
    }
  };

  const getGradeColor = (grade: string | null): string => {
    if (!grade) return '#6c757d';
    const colors: Record<string, string> = {
      A: '#28a745',
      B: '#17a2b8',
      C: '#ffc107',
      D: '#fd7e14',
      F: '#dc3545',
    };
    return colors[grade] || '#6c757d';
  };

  const getScoreDisplay = (score: number | null): string => {
    if (score === null || score === undefined) return '-';
    return score.toString();
  };

  const getDivisionBadge = (division: string | null): string => {
    if (!division) return '-';
    return division;
  };

  // SVG Icons
  const PrintIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M19 8H5C3.89543 8 3 8.89543 3 10V16C3 17.1046 3.89543 18 5 18H8M19 8V18H16M19 8H16M16 8V4H8V8M8 18H16M8 18V20C8 21.1046 8.89543 22 10 22H14C15.1046 22 16 21.1046 16 20V18" stroke="currentColor" strokeWidth="1.5"/>
      <circle cx="16" cy="13" r="1" fill="currentColor"/>
    </svg>
  );

  const BackIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M19 12H5M5 12L12 19M5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  );

  const SpinnerIcon = () => (
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="12" r="10" stroke="#e8ecf1" strokeWidth="4"/>
      <path d="M12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22" stroke="#0d47a1" strokeWidth="4" strokeLinecap="round"/>
    </svg>
  );

  const useReactToPrint = require('react-to-print').useReactToPrint;

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `Marks_Report_${record?.school_name || 'Class'}_${record?.exam_year || ''}`,
    onAfterPrint: () => console.log('Print completed'),
    pageStyle: `
      @page {
        size: landscape;
        margin: 10mm 8mm 10mm 8mm;
      }
      @media print {
        body {
          margin: 0;
          padding: 0;
        }
      }
    `,
  });

  if (loading) {
    return (
      <div className="cmp-loading">
        <div className="cmp-spinner">
          <SpinnerIcon />
        </div>
        <p>Loading...</p>
      </div>
    );
  }

  if (error || !record) {
    return (
      <div className="cmp-error">
        <h2>Error</h2>
        <p>{error || 'Record not found'}</p>
        <button onClick={() => navigate(`/dashboard/class-marks/${id}`)}>Back</button>
      </div>
    );
  }

  return (
    <div className="cmp-page">
      <div className="cmp-header">
        <h1>Class Marks Report</h1>
        <div className="cmp-actions">
          <button className="cmp-btn cmp-btn-print" onClick={handlePrint}>
            <PrintIcon /> Print
          </button>
          <button className="cmp-btn cmp-btn-back" onClick={() => navigate(`/dashboard/class-marks/${id}`)}>
            <BackIcon /> Back
          </button>
        </div>
      </div>

      {/* Content to print - wrapped in ref */}
      <div className="cmp-content" ref={printRef}>
        {/* School Header */}
        <div className="cmp-school-header">
          <h2>{record.school_name}</h2>
          {record.school_registration_number && (
            <p>Reg. No: {record.school_registration_number}</p>
          )}
          <div className="cmp-exam-info">
            <span>Class: {record.class_display}</span>
            <span>Term: {record.term_display}</span>
            <span>Exam: {record.exam_type_display} {record.exam_year}</span>
            {record.exam_month && <span>Month: {record.exam_month}</span>}
            {record.center_name && <span>Center: {record.center_name}</span>}
          </div>
          <div className="cmp-status-info">
            <span>Status: {record.status_display}</span>
            <span>Total Students: {record.students?.length || 0}</span>
            <span>Generated: {new Date().toLocaleDateString()}</span>
          </div>
        </div>

        {/* Overall Summary */}
        {record.overall_summary && (
          <div className="cmp-overall-summary">
            <h3>Overall Performance Summary</h3>
            <div className="cmp-summary-stats">
              <div className="cmp-stat-item">
                <span className="cmp-stat-label">Total Students</span>
                <span className="cmp-stat-value">{record.overall_summary.total_students}</span>
              </div>
              <div className="cmp-stat-item">
                <span className="cmp-stat-label">Male</span>
                <span className="cmp-stat-value">{record.overall_summary.total_male}</span>
              </div>
              <div className="cmp-stat-item">
                <span className="cmp-stat-label">Female</span>
                <span className="cmp-stat-value">{record.overall_summary.total_female}</span>
              </div>
              <div className="cmp-stat-item">
                <span className="cmp-stat-label">Class Average</span>
                <span className="cmp-stat-value">{record.overall_summary.class_average?.toFixed(1) || '-'}</span>
              </div>
              <div className="cmp-stat-item">
                <span className="cmp-stat-label">Class GPA</span>
                <span className="cmp-stat-value">{record.overall_summary.class_gpa?.toFixed(2) || '-'}</span>
              </div>
              <div className="cmp-stat-item">
                <span className="cmp-stat-label">Pass Rate</span>
                <span className="cmp-stat-value">{record.overall_summary.pass_percentage?.toFixed(1) || 0}%</span>
              </div>
              <div className="cmp-stat-item">
                <span className="cmp-stat-label">Passed</span>
                <span className="cmp-stat-value">{record.overall_summary.pass_count}</span>
              </div>
              <div className="cmp-stat-item">
                <span className="cmp-stat-label">Failed</span>
                <span className="cmp-stat-value">{record.overall_summary.fail_count}</span>
              </div>
            </div>
          </div>
        )}

        {/* Division Distribution */}
        {record.overall_summary && Object.keys(record.overall_summary.division_counts).length > 0 && (
          <div className="cmp-division-summary">
            <h3>Division Distribution</h3>
            <div className="cmp-division-grid">
              {Object.entries(record.overall_summary.division_counts).map(([division, count]) => (
                <div key={division} className="cmp-division-item">
                  <span className="cmp-division-label">Division {division}</span>
                  <span className="cmp-division-count">{count}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Student Marks Table - SEPARATE SCORE AND GRADE COLUMNS */}
        <div className="cmp-table-container">
          <h3>Student Marks List</h3>
          <table className="cmp-table">
            <thead>
              <tr>
                <th rowSpan={2} className="cmp-col-sn">#</th>
                <th rowSpan={2} className="cmp-col-name">Name</th>
                <th rowSpan={2} className="cmp-col-sex">Sex</th>
                {subjects.map((subject) => (
                  <th key={subject.key} colSpan={2} className="cmp-col-subject">{subject.label}</th>
                ))}
                <th rowSpan={2} className="cmp-col-total">Total</th>
                <th rowSpan={2} className="cmp-col-avg">Avg</th>
                <th rowSpan={2} className="cmp-col-div">Div</th>
                <th rowSpan={2} className="cmp-col-points">Pts</th>
              </tr>
              <tr>
                {subjects.map((subject) => (
                  <React.Fragment key={`${subject.key}-sub`}>
                    <th className="cmp-sub-header-score">S</th>
                    <th className="cmp-sub-header-grade">G</th>
                  </React.Fragment>
                ))}
              </tr>
            </thead>
            <tbody>
              {record.students && record.students.length > 0 ? (
                record.students.map((student, index) => (
                  <tr key={student.id} className={index % 2 === 0 ? 'cmp-even-row' : 'cmp-odd-row'}>
                    <td className="cmp-col-sn">{index + 1}</td>
                    <td className="cmp-col-name">{student.candidate_name}</td>
                    <td className="cmp-col-sex">{student.sex}</td>
                    {subjects.map((subject) => {
                      const score = student[subject.key as keyof StudentMarks] as number | null;
                      const gradeKey = subject.key.replace('_score', '_grade');
                      const grade = student[gradeKey as keyof StudentMarks] as string | null;
                      return (
                        <React.Fragment key={subject.key}>
                          <td className="cmp-score-cell">{getScoreDisplay(score)}</td>
                          <td className="cmp-grade-cell">
                            <span className="cmp-grade-badge" style={{ backgroundColor: getGradeColor(grade) }}>
                              {grade || '-'}
                            </span>
                          </td>
                        </React.Fragment>
                      );
                    })}
                    <td className="cmp-col-total">{student.total_score?.toFixed(1) || '-'}</td>
                    <td className="cmp-col-avg">{student.average_score?.toFixed(1) || '-'}</td>
                    <td className="cmp-col-div">{getDivisionBadge(student.division)}</td>
                    <td className="cmp-col-points">{student.points || '-'}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={subjects.length * 2 + 8} className="cmp-no-data">
                    No students found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Subject Summaries */}
        {record.subject_summaries && record.subject_summaries.length > 0 && (
          <div className="cmp-subject-summary-container">
            <h3>Subject Performance Summary</h3>
            <table className="cmp-subject-summary-table">
              <thead>
                <tr>
                  <th>Subject</th>
                  <th>Total</th>
                  <th>Passed</th>
                  <th>Failed</th>
                  <th>Pass %</th>
                  <th>Avg Score</th>
                </tr>
              </thead>
              <tbody>
                {record.subject_summaries.map((subject) => (
                  <tr key={subject.subject_name}>
                    <td>{subject.subject_name}</td>
                    <td>{subject.total_candidates}</td>
                    <td>{subject.total_passed}</td>
                    <td>{subject.total_failed}</td>
                    <td>{subject.pass_percentage?.toFixed(1) || 0}%</td>
                    <td>{subject.average_score?.toFixed(1) || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Footer */}
        <div className="cmp-footer">
          <p>Generated on: {new Date().toLocaleString()}</p>
          <p>© {new Date().getFullYear()} ZiTech Solutions - Class Marks Management System</p>
        </div>
      </div>
    </div>
  );
};

export default ClassMarksPrint;